import SelectedCustomer from '@/platform/common/components/SelectedCustomer/SelectedCustomer';
import {
    Button,
    Container,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Message,
    Segment,
    SegmentContent,
    SegmentHeader,
    Toggle,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import Lines from './components/Lines/Lines';

const CreateOrUpdateView = () => {
    const { auth, invoice, contacts, hasContracts } = usePage().props;
    const { data, setData, post, patch, errors, processing } = useForm({});

    useEffect(() => {
        if (invoice?.id) {
            setData(invoice);
        }
    }, [invoice?.id]);

    const handleSubmit = () => {
        if (invoice?.id) {
            patch(`/customers/${invoice.customer_id}/invoices/${invoice.id}`);

            return;
        }

        post(`/customers/${auth.customer.id}/invoices`);
    };

    return (
        <Container>
            <SelectedCustomer />

            {!hasContracts && (
                <Message
                    className='mb-4'
                    variant='error'
                    content='Deze klant heeft geen contract getekend. Weet je zeker dat we voor deze klant kunnen factureren?'
                />
            )}

            {invoice?.mailed_at && (
                <Message
                    className='mb-4'
                    variant='error'
                    content='Deze factuur is al gemaild naar de klant. Weet je zeker dat je deze nog wilt aanpassen?'
                />
            )}

            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader
                        header={`Factuur ${invoice?.id ? 'bijwerken' : 'aanmaken'}`}
                    >
                        <Button
                            as={Link}
                            href={`/customers/${auth?.user?.selected_customer_id}/invoices`}
                        >
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FormGroup inline equal>
                            {invoice?.id && (
                                <Input
                                    label='Factuurnummer'
                                    value={invoice?.number || ''}
                                    readOnly
                                />
                            )}
                            <Dropdown
                                label='Entiteit'
                                required
                                options={auth?.customer?.entities?.map(
                                    entity => ({
                                        id: entity.id,
                                        name: entity.name,
                                    })
                                )}
                                value={data?.customer_entity_id || []}
                                onChange={value =>
                                    setData('customer_entity_id', value)
                                }
                                error={errors?.customer_entity_id}
                            />
                        </FormGroup>

                        <FormGroup inline equal>
                            <Input
                                label='PO nummer'
                                value={data?.po_no || ''}
                                onChange={event =>
                                    setData('po_no', event?.target?.value)
                                }
                                error={errors?.po_no}
                            />

                            <Deferred
                                data='contacts'
                                fallback={
                                    <InputPlaceholder
                                        label='Contactpersoon'
                                        placeholder='Selecteer een contact'
                                    />
                                }
                            >
                                <Dropdown
                                    label='Contactpersoon'
                                    options={contacts?.map(contact => ({
                                        id: contact.id,
                                        name: contact.name,
                                    }))}
                                    value={data?.contact_id || []}
                                    onChange={value =>
                                        setData('contact_id', value)
                                    }
                                    error={errors?.contact_id}
                                />
                            </Deferred>
                        </FormGroup>

                        <FormGroup>
                            <Toggle
                                label='Automatisch incasso'
                                checked={!!data?.is_sepa || false}
                                onChange={checked =>
                                    setData('is_sepa', checked)
                                }
                            />
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <Lines data={data} onChange={setData} />

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
